import { useState } from 'react';
import type { User } from '../types/user';
import { UserRole } from '../types/user';

interface UserCardProps {
  user: User;
  onDelete?: (userId: number) => void;
  onDisable?: (userId: number) => void;
  onEnable?: (userId: number) => void;
  onResetPassword?: (userId: number, newPassword: string) => void;
}

const getRoleDisplay = (role: UserRole): string => {
  switch (role) {
    case UserRole.ADMIN:
      return 'Admin';
    case UserRole.MANAGER:
      return 'Manager';
    case UserRole.STANDARD:
      return 'Standard';
    case UserRole.READONLY:
      return 'Read Only';
    default:
      return 'Standard';
  }
};

const getRoleBadgeColor = (role: UserRole): string => {
  switch (role) {
    case UserRole.ADMIN:
      return 'bg-black text-white';
    case UserRole.MANAGER:
      return 'bg-gray-700 text-white';
    case UserRole.STANDARD:
      return 'bg-gray-400 text-white';
    case UserRole.READONLY:
      return 'bg-gray-300 text-gray-700';
    default:
      return 'bg-gray-400 text-white';
  }
};

export default function UserCard({ user, onDelete, onDisable, onEnable, onResetPassword }: UserCardProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPasswordField, setShowConfirmPasswordField] = useState(false);

  const handleDelete = () => {
    if (onDelete) {
      onDelete(user.id);
      setShowConfirm(false);
    }
  };

  const generateStrongPassword = () => {
    const length = 16;
    // Exclude similar characters (1, i, l, I, L, o, O, 0) and limit special chars
    const uppercase = "ABCDEFGHJKMNPQRSTUVWXYZ"; // Excluded: I, L, O
    const lowercase = "abcdefghjkmnpqrstuvwxyz"; // Excluded: i, l, o
    const numbers = "23456789"; // Excluded: 0, 1
    const special = "#@$&!?%+="; // Only easy-to-type special chars
    const charset = uppercase + lowercase + numbers + special;
    let password = "";

    // Ensure at least one of each type
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += special[Math.floor(Math.random() * special.length)];

    // Fill the rest randomly
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }

    // Shuffle the password
    password = password.split('').sort(() => Math.random() - 0.5).join('');

    setNewPassword(password);
    setConfirmPassword(password);
    setShowNewPassword(true);
    setShowConfirmPasswordField(true);
    setPasswordError('');
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 card-hover shadow-subtle">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-medium">{user.username}</h3>
          <p className="text-sm text-gray-600">{user.email}</p>
        </div>
        <span className={`px-2 py-1 text-xs rounded ${getRoleBadgeColor(user.role)}`}>
          {getRoleDisplay(user.role)}
        </span>
      </div>

      {user.full_name && (
        <p className="text-sm text-gray-700 mb-4">{user.full_name}</p>
      )}

      <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
        <span>ID: {user.id}</span>
        <span className={user.is_active ? 'text-green-600' : 'text-red-600'}>
          {user.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div className="text-xs text-gray-400">
        <div>Created: {new Date(user.created_at).toLocaleDateString()}</div>
      </div>

      {(onDelete || onDisable || onEnable || onResetPassword) && (
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
          {/* Disable/Enable Button */}
          {(onDisable || onEnable) && (
            <button
              onClick={() => {
                if (user.is_active && onDisable) {
                  onDisable(user.id);
                } else if (!user.is_active && onEnable) {
                  onEnable(user.id);
                }
              }}
              className={`w-full px-3 py-2 text-sm rounded ${
                user.is_active
                  ? 'text-orange-600 border border-orange-200 hover:bg-orange-50'
                  : 'text-green-600 border border-green-200 hover:bg-green-50'
              }`}
            >
              {user.is_active ? 'Disable User' : 'Enable User'}
            </button>
          )}

          {/* Reset Password Button */}
          {onResetPassword && (
            <>
              {showPasswordReset ? (
                <div className="bg-blue-50 p-3 rounded text-xs space-y-2">
                  <div className="flex justify-between items-center">
                    <p className="font-medium text-blue-900">Reset Password for {user.username}</p>
                    <button
                      type="button"
                      onClick={generateStrongPassword}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      ⚡ Generate
                    </button>
                  </div>

                  <div>
                    <label className="block text-gray-700 mb-1">New Password</label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          setPasswordError('');
                        }}
                        placeholder="Enter new password"
                        className="w-full px-2 py-1 pr-8 border border-gray-300 rounded text-sm"
                        minLength={8}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-xs"
                      >
                        {showNewPassword ? '🙈' : '👁️'}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-700 mb-1">Confirm Password</label>
                    <div className="relative">
                      <input
                        type={showConfirmPasswordField ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          setPasswordError('');
                        }}
                        placeholder="Confirm new password"
                        className="w-full px-2 py-1 pr-8 border border-gray-300 rounded text-sm"
                        minLength={8}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPasswordField(!showConfirmPasswordField)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-xs"
                      >
                        {showConfirmPasswordField ? '🙈' : '👁️'}
                      </button>
                    </div>
                  </div>

                  {passwordError && (
                    <p className="text-red-600 text-xs">{passwordError}</p>
                  )}

                  <div className="flex space-x-2 pt-1">
                    <button
                      onClick={() => {
                        if (!newPassword || newPassword.length < 8) {
                          setPasswordError('Password must be at least 8 characters');
                          return;
                        }
                        if (newPassword !== confirmPassword) {
                          setPasswordError('Passwords do not match');
                          return;
                        }
                        onResetPassword(user.id, newPassword);
                        setShowPasswordReset(false);
                        setNewPassword('');
                        setConfirmPassword('');
                        setPasswordError('');
                      }}
                      className="flex-1 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Reset Password
                    </button>
                    <button
                      onClick={() => {
                        setShowPasswordReset(false);
                        setNewPassword('');
                        setConfirmPassword('');
                        setPasswordError('');
                      }}
                      className="flex-1 px-3 py-2 btn-secondary rounded"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowPasswordReset(true)}
                  className="w-full px-3 py-2 text-sm text-blue-600 border border-blue-200 rounded hover:bg-blue-50"
                >
                  Reset Password
                </button>
              )}
            </>
          )}

          {/* Delete Button */}
          {onDelete && (
            <>
              {showConfirm ? (
                <div className="flex space-x-2">
                  <button
                    onClick={handleDelete}
                    className="flex-1 px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                  >
                    Confirm Delete
                  </button>
                  <button
                    onClick={() => setShowConfirm(false)}
                    className="flex-1 px-3 py-2 btn-secondary text-sm rounded"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowConfirm(true)}
                  className="w-full px-3 py-2 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50"
                >
                  Delete User
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
